import { useEffect, useRef, useState } from "react";
import {
  FileText,
  Upload,
  Trash2,
  RefreshCcw,
  Send,
  Search,
  Download,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/auth";

const AUDIENCE_OPTIONS = [
  { value: "either", label: "Either" },
  { value: "couple", label: "Couples" },
  { value: "vendor", label: "Vendors" },
];

const AUDIENCE_PILL_TONE = {
  couple: "border-violet-700/40 text-violet-700",
  vendor: "border-emerald-700/40 text-emerald-700",
  either: "border-[var(--ld-gold-deep)]/40 text-[var(--ld-gold-deep)]",
};

/** Inline editable audience dropdown. Persists change immediately via PATCH. */
const AudienceSelect = ({ doc, onChange }) => {
  const [saving, setSaving] = useState(false);
  const tone = AUDIENCE_PILL_TONE[doc.audience] || AUDIENCE_PILL_TONE.either;
  const handle = async (e) => {
    const next = e.target.value;
    if (next === doc.audience) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/documents/${doc.id}`, {
        audience: next,
      });
      onChange?.(data);
      toast(`Audience updated → ${data.audience}`, { duration: 1800 });
    } catch (err) {
      toast(err?.response?.data?.detail || "Could not update audience", {
        duration: 2400,
      });
    } finally {
      setSaving(false);
    }
  };
  return (
    <select
      data-testid={`doc-audience-select-${doc.id}`}
      value={doc.audience || "either"}
      onChange={handle}
      disabled={saving}
      className={`inline-flex items-center px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase border bg-transparent appearance-none cursor-pointer ${tone} hover:border-[var(--ld-ink)]/60 transition-colors disabled:opacity-60`}
    >
      {AUDIENCE_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
};

const SendDocModal = ({ doc, onClose, onSent }) => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  if (!doc) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/documents/send", {
        document_id: doc.id,
        recipient_email: email.trim(),
        message: message.trim() || undefined,
      });
      toast(
        data.sent
          ? `Sent “${doc.title}” to ${data.recipient}`
          : `Logged — Resend not configured (${data.recipient})`,
        { duration: 2800 }
      );
      onSent?.();
      onClose();
    } catch (err) {
      toast(err?.response?.data?.detail || "Send failed", { duration: 2800 });
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="doc-send-modal"
      className="fixed inset-0 z-50 bg-[var(--ld-ink)]/70 backdrop-blur-sm flex items-center justify-center px-4"
    >
      <div className="bg-[var(--ld-ivory)] border border-[var(--ld-border)] w-full max-w-md p-6">
        <span className="font-accent text-[10px] tracking-[0.25em] uppercase text-[var(--ld-gold-deep)]">
          Send document
        </span>
        <h3 className="font-serif-display text-2xl mt-2 text-[var(--ld-ink)]">{doc.title}</h3>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--ld-ink-muted)]">
              Recipient email *
            </span>
            <input
              type="email"
              required
              data-testid="doc-send-email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="couple@example.com"
              className="h-10 px-3 text-sm bg-white border border-[var(--ld-ink)]/25"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--ld-ink-muted)]">
              Personal note (optional)
            </span>
            <textarea
              data-testid="doc-send-message"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="As discussed on our call…"
              className="px-3 py-2 text-sm bg-white border border-[var(--ld-ink)]/25 resize-y"
            />
          </label>
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[11px] tracking-[0.22em] uppercase border border-[var(--ld-ink)]/25 text-[var(--ld-ink)] hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="doc-send-submit"
              disabled={sending}
              className="inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.22em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] hover:bg-black disabled:opacity-60"
            >
              {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} strokeWidth={1.7} />}
              {sending ? "Sending…" : "Send"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DocumentsPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [audience, setAudience] = useState("either");
  const [q, setQ] = useState("");
  const [sendTarget, setSendTarget] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/documents");
      setRows(r.data || []);
    } catch (e) {
      toast("Couldn't load documents", { duration: 2400 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
     
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast("Pick a file first", { duration: 2200 });
      return;
    }
    if (!title.trim()) {
      toast("Give the document a title", { duration: 2200 });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("audience", audience);
      fd.append("file", file);
      await api.post("/admin/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast("Document uploaded", { duration: 2000 });
      setTitle("");
      setAudience("either");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast(err?.response?.data?.detail || "Upload failed", { duration: 2800 });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this document permanently?")) return;
    try {
      await api.delete(`/admin/documents/${id}`);
      toast("Deleted", { duration: 1800 });
      load();
    } catch (err) {
      toast("Delete failed", { duration: 2400 });
    }
  };

  const fmtSize = (n) => {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };

  const filtered = rows.filter((r) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (
      (r.title || "").toLowerCase().includes(s) ||
      (r.filename || "").toLowerCase().includes(s)
    );
  });

  return (
    <div data-testid="section-documents">
      <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
        Workspace
      </span>
      <h2 className="font-serif-display mt-3 text-3xl sm:text-4xl text-[var(--ld-ink)] font-medium leading-tight">
        Documents
      </h2>
      <p className="mt-2 text-sm text-[var(--ld-ink-muted)] max-w-2xl">
        Your library of welcome packs, contracts and guides. Upload once, then send to any
        couple or vendor from the Vendor / Couple list dropdowns.
      </p>

      {/* Upload form */}
      <form
        onSubmit={handleUpload}
        data-testid="doc-upload-form"
        className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-3 border border-[var(--ld-border)] bg-[var(--ld-ivory-warm)]/30 p-4"
      >
        <label className="md:col-span-5 flex flex-col gap-1">
          <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--ld-ink-muted)]">
            Title *
          </span>
          <input
            type="text"
            required
            data-testid="doc-upload-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Couple Welcome Pack"
            className="h-10 px-3 text-sm bg-white border border-[var(--ld-ink)]/25"
          />
        </label>
        <label className="md:col-span-2 flex flex-col gap-1">
          <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--ld-ink-muted)]">
            Audience
          </span>
          <select
            data-testid="doc-upload-audience"
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            className="h-10 px-2 text-sm bg-white border border-[var(--ld-ink)]/25"
          >
            {AUDIENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="md:col-span-4 flex flex-col gap-1">
          <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--ld-ink-muted)]">
            File (≤ 20 MB)
          </span>
          <input
            type="file"
            ref={fileRef}
            data-testid="doc-upload-file"
            className="text-sm file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-[var(--ld-ink)] file:text-[var(--ld-gold)] file:text-[10px] file:tracking-[0.2em] file:uppercase"
          />
        </label>
        <div className="md:col-span-1 flex items-end">
          <button
            type="submit"
            disabled={uploading}
            data-testid="doc-upload-submit"
            className="inline-flex items-center gap-2 h-10 px-4 text-[11px] tracking-[0.22em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] hover:bg-black disabled:opacity-60 w-full justify-center"
          >
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {uploading ? "Up…" : "Up"}
          </button>
        </div>
      </form>

      {/* Search + refresh */}
      <div className="mt-6 flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ld-ink-muted)]" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search documents…"
            className="w-full h-11 bg-transparent border border-[var(--ld-ink)]/25 pl-9 pr-3 text-sm text-[var(--ld-ink)] placeholder:text-[var(--ld-ink-muted)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors"
          />
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)]"
        >
          <RefreshCcw size={12} strokeWidth={1.6} />
          Refresh
        </button>
      </div>

      {/* Table */}
      <div className="mt-5 border border-[var(--ld-border)]/80 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="documents-table">
            <thead className="bg-[var(--ld-ink)]/[0.03] text-[10px] tracking-[0.18em] uppercase text-[var(--ld-ink-muted)]">
              <tr>
                <th className="text-left font-accent font-normal px-4 py-3">Title</th>
                <th className="text-left font-accent font-normal px-4 py-3">Audience</th>
                <th className="text-left font-accent font-normal px-4 py-3">File</th>
                <th className="text-right font-accent font-normal px-4 py-3">Size</th>
                <th className="text-right font-accent font-normal px-4 py-3">Uploaded</th>
                <th className="text-right font-accent font-normal px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--ld-border)]/70">
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-[var(--ld-ink-muted)]">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center italic text-[var(--ld-ink-muted)]">
                    {rows.length === 0 ? "No documents yet." : "No matches."}
                  </td>
                </tr>
              )}
              {filtered.map((d) => (
                <tr
                  key={d.id}
                  data-testid={`doc-row-${d.id}`}
                  className="hover:bg-[var(--ld-ink)]/[0.02]"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-[var(--ld-ink)] flex items-center gap-2">
                      <FileText size={14} strokeWidth={1.5} className="text-[var(--ld-gold-deep)]" />
                      {d.title}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <AudienceSelect
                      doc={d}
                      onChange={(updated) =>
                        setRows((arr) =>
                          arr.map((r) => (r.id === updated.id ? updated : r))
                        )
                      }
                    />
                  </td>
                  <td className="px-4 py-3 text-[12px] text-[var(--ld-ink-muted)]">
                    <a
                      href={d.file_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`doc-download-${d.id}`}
                      className="hover:text-[var(--ld-ink)] underline underline-offset-4 decoration-[var(--ld-gold-deep)] inline-flex items-center gap-1"
                    >
                      <Download size={11} />
                      {d.filename}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmtSize(d.size_bytes)}</td>
                  <td className="px-4 py-3 text-right text-[12px] text-[var(--ld-ink-muted)]">
                    {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <a
                        href={d.file_url}
                        download={d.filename}
                        data-testid={`doc-download-btn-${d.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase border border-[var(--ld-ink)]/25 text-[var(--ld-ink)] hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)] transition-colors"
                      >
                        <Download size={11} strokeWidth={1.7} />
                        Download
                      </a>
                      <button
                        type="button"
                        data-testid={`doc-send-${d.id}`}
                        onClick={() => setSendTarget(d)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase border border-[var(--ld-gold-deep)]/40 text-[var(--ld-gold-deep)] hover:bg-[var(--ld-gold-deep)] hover:text-white transition-colors"
                      >
                        <Send size={11} strokeWidth={1.7} />
                        Send
                      </button>
                      <button
                        type="button"
                        data-testid={`doc-delete-${d.id}`}
                        onClick={() => handleDelete(d.id)}
                        aria-label="Delete document"
                        className="inline-flex h-7 w-7 items-center justify-center text-rose-500 hover:text-rose-700"
                      >
                        <Trash2 size={13} strokeWidth={1.6} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <SendDocModal doc={sendTarget} onClose={() => setSendTarget(null)} onSent={load} />
    </div>
  );
};

export default DocumentsPanel;
